import { useEffect, useRef, useState } from "react";
import { userApi } from "./api";
import { Button } from "./ui";

export type CreditOperation = "master_ai_generate" | "image2_generate" | "image2_revision";

export interface CreditPricing {
  revision: number;
  masterAiGeneratePerSlide: number;
  image2GeneratePerSlide: number;
  image2RevisionPerSlide: number;
}

export interface CreditAccount {
  userId: number;
  balance: number;
  totalGranted: number;
  totalConsumed: number;
  totalRefunded: number;
  updatedAt: string;
}

export interface CreditTransaction {
  id: string;
  generationId?: string;
  kind: "grant" | "consume" | "refund";
  reasonCode: string;
  delta: number;
  balanceAfter: number;
  note?: string;
  createdAt: string;
}

export interface CreditOverview {
  ok: true;
  account: CreditAccount;
  transactions: CreditTransaction[];
  pricing: CreditPricing;
}

export interface CreditSpendQuote {
  operation: CreditOperation;
  label: string;
  slideCount: number;
  pointsPerSlide: number;
  totalPoints: number;
  balance: number;
  billingNotice?: string;
}

const CREDIT_INSUFFICIENT_EVENT = "pptdeep:credit-insufficient";
const CREDIT_CHANGED_EVENT = "pptdeep:credits-changed";

interface InsufficientCreditNotice {
  message: string;
  required?: number;
  available?: number;
  billingNotice?: string;
}

export async function requestCreditSpend(input: {
  operation: CreditOperation;
  slideCount?: number;
  fixedPoints?: number;
  billingNotice?: string;
}): Promise<CreditSpendQuote | undefined> {
  const overview = await userApi<CreditOverview>("/api/app/credits", { cache: "no-store" });
  const slideCount = positiveInteger(input.slideCount || 1);
  const pointsPerSlide = operationPoints(overview.pricing, input.operation);
  const totalPoints = input.fixedPoints === undefined
    ? pointsPerSlide * slideCount
    : nonNegativeInteger(input.fixedPoints);
  const quote: CreditSpendQuote = {
    operation: input.operation,
    label: operationLabel(input.operation),
    slideCount,
    pointsPerSlide,
    totalPoints,
    balance: overview.account.balance,
    ...(input.billingNotice?.trim() ? { billingNotice: input.billingNotice.trim().slice(0, 500) } : {}),
  };
  if (totalPoints === 0) return quote;
  if (!hasSufficientCredits(overview.account.balance, totalPoints)) {
    notifyInsufficientCredits({
      message: `积分不足，本次需要 ${totalPoints} 积分，当前可用 ${overview.account.balance} 积分`,
      required: totalPoints,
      available: overview.account.balance,
      billingNotice: quote.billingNotice,
    });
    return undefined;
  }
  return quote;
}

export function hasSufficientCredits(balance: number, totalPoints: number): boolean {
  return nonNegativeInteger(balance) >= nonNegativeInteger(totalPoints);
}

export function notifyCreditActionSubmitted(): void {
  window.dispatchEvent(new Event(CREDIT_CHANGED_EVENT));
  window.setTimeout(() => window.dispatchEvent(new Event(CREDIT_CHANGED_EVENT)), 3_000);
}

export function notifyInsufficientCredits(notice: InsufficientCreditNotice): void {
  window.dispatchEvent(new CustomEvent(CREDIT_INSUFFICIENT_EVENT, { detail: notice }));
}

export function subscribeCreditChanges(listener: () => void): () => void {
  window.addEventListener(CREDIT_CHANGED_EVENT, listener);
  return () => window.removeEventListener(CREDIT_CHANGED_EVENT, listener);
}

export function creditKindLabel(kind: CreditTransaction["kind"]): string {
  return ({ grant: "赠送", consume: "消耗", refund: "退回" } as const)[kind];
}

export function creditReasonLabel(reason: string): string {
  return ({
    signup_gift: "注册赠送",
    admin_gift: "管理员赠送",
    membership_gift: "会员赠送",
    master_ai_generate: "大师 AI 生成",
    image2_generate: "图片生成",
    image2_revision: "单页修改",
    generation_failed: "生成失败退回",
    generation_cancelled: "取消生成退回",
  } as Record<string, string>)[reason] || reason;
}

export function CreditInteractionHost() {
  const [insufficient, setInsufficient] = useState<InsufficientCreditNotice>();
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onInsufficient = (event: Event) => {
      const detail = (event as CustomEvent<InsufficientCreditNotice>).detail;
      if (!detail?.message) return;
      previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setInsufficient(detail);
    };
    window.addEventListener(CREDIT_INSUFFICIENT_EVENT, onInsufficient);
    return () => {
      window.removeEventListener(CREDIT_INSUFFICIENT_EVENT, onInsufficient);
    };
  }, []);

  useEffect(() => {
    if (!insufficient) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeInsufficient();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [insufficient]);

  const closeInsufficient = () => {
    setInsufficient(undefined);
    window.requestAnimationFrame(() => previousFocus.current?.focus());
  };

  return <>
    {insufficient && <div className="credit-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="credit-insufficient-title" aria-describedby="credit-insufficient-description" onMouseDown={(event) => {
      if (event.target === event.currentTarget) closeInsufficient();
    }}>
      <section className="credit-confirm-dialog credit-insufficient-dialog">
        <span>积分不足</span>
        <h2 id="credit-insufficient-title">当前积分不足，无法开始生成</h2>
        <p id="credit-insufficient-description">{insufficient.message}</p>
        {insufficient.billingNotice && <aside className="credit-billing-notice">{insufficient.billingNotice}</aside>}
        {typeof insufficient.required === "number" && typeof insufficient.available === "number" && <dl>
          <div><dt>本次需要</dt><dd>{insufficient.required}</dd></div>
          <div><dt>当前可用</dt><dd>{insufficient.available}</dd></div>
          <div><dt>还差积分</dt><dd>{Math.max(0, insufficient.required - insufficient.available)}</dd></div>
        </dl>}
        <small>请补充积分后，再点击“继续生成”；已经完成的解析不会重复执行。</small>
        <div>
          <Button variant="secondary" onClick={closeInsufficient}>暂不购买</Button>
          <Button autoFocus onClick={() => {
            window.open("/app/account#membership", "_blank", "noopener");
            closeInsufficient();
          }}>购买积分</Button>
        </div>
      </section>
    </div>}
  </>;
}

function operationPoints(pricing: CreditPricing, operation: CreditOperation): number {
  return {
    master_ai_generate: pricing.masterAiGeneratePerSlide,
    image2_generate: pricing.image2GeneratePerSlide,
    image2_revision: pricing.image2RevisionPerSlide,
  }[operation];
}

function operationLabel(operation: CreditOperation): string {
  return {
    master_ai_generate: "大师 AI 生成",
    image2_generate: "图片生成",
    image2_revision: "单页修改",
  }[operation];
}

function positiveInteger(value: number): number {
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error("无法确定本次生成页数，请刷新后重试");
  return value;
}

function nonNegativeInteger(value: number): number {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error("本次任务的积分金额无效，请刷新后重试");
  return value;
}
